import { ENDPOINT_STATISTICS } from '@/common/constants';
import { HttpApi } from '@/common/http';
import { Filter } from '@/common/icons/Filter';
import { IconSheet } from '@/common/icons/SheetIcon';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import useStore from '@/lib/store';
import { Trash2 } from 'lucide-react';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { IReportResponse } from '../models';
import useReportStore from '../store/report';
import { generateExcel } from '../utils/excelGenerator';
import {
  createColumns as createPartyColumn,
  PartiesTable,
} from './parties-table';
import {
  createColumns as createCargoColumn,
  ReportsTable,
} from './reports-table';
import ReportsFilterModal from './ReportsFilterGrainModal';

const httpApi = new HttpApi();

export const ReportsGrainWrapper: FC = () => {
  const { user } = useStore();
  const { t } = useTranslation();
  const { fetchReports, reports, fetchReportParties, reportParties } =
    useReportStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'cargo_report' | 'party_report'>(
    'cargo_report',
  );

  const cargoColumn = createCargoColumn();
  const partyColumn = createPartyColumn();

  const pageSize = Number(searchParams.get('size')) || 10;
  const currentPage = Number(searchParams.get('page')) || 1;

  useEffect(() => {
    const queryParams: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      if (key !== 'report_type') {
        queryParams[key] = value;
      }
    });

    if (activeTab === 'cargo_report') {
      fetchReports({ ...queryParams, page: currentPage, size: pageSize });
    } else if (activeTab === 'party_report') {
      fetchReportParties(queryParams);
    }
  }, [searchParams, activeTab, currentPage, pageSize]);

  const handleExport = async () => {
    if (activeTab === 'cargo_report' && reports?.content?.length) {
      const response = await httpApi.get<IReportResponse>(ENDPOINT_STATISTICS);
      await generateExcel(response, cargoColumn, t('reports'));
    } else if (activeTab === 'party_report' && reportParties?.content?.length) {
      await generateExcel(reportParties, partyColumn, t('party.reports'));
    }
  };

  const handleClear = () => {
    console.log('clear');
    // const keepParams = new URLSearchParams();

    // const page = searchParams.get('page');
    // const size = searchParams.get('size');

    // if (page) keepParams.set('page', page);
    // if (size) keepParams.set('size', size);

    // setSearchParams(keepParams);
  };

  return (
    <div className="flex flex-col py-8 px-10 w-full space-y-6">
      <Tabs
        defaultValue="cargo_report"
        onValueChange={(value) => {
          setActiveTab(value as 'cargo_report' | 'party_report');
          setSearchParams({});
        }}
      >
        <TabsList>
          <TabsTrigger value="cargo_report">{t('cargo.report')}</TabsTrigger>
          <TabsTrigger value="party_report">{t('party.report')}</TabsTrigger>
        </TabsList>

        <div>
          <h2 className="text-center text-1.5xl font-medium mt-6">
            {user?.place_name}
          </h2>
          <div className="flex justify-end">
            <div className="flex gap-2">
              <ReportsFilterModal type={activeTab}>
                <Button
                  variant="outline"
                  className="text-gray-900 text-sm font-medium"
                >
                  <Filter />
                  {t('filter')}
                </Button>
              </ReportsFilterModal>
              <Button
                variant="outline"
                className="text-gray-900 text-sm font-medium"
                onClick={handleClear}
              >
                <Trash2 size={18} color="red" />
                {t('filter.clear')}
              </Button>
              <Button
                variant="outline"
                className="text-gray-900 text-sm font-medium"
                onClick={handleExport}
              >
                <IconSheet />
                {t('download.excel')}
              </Button>
            </div>
          </div>
        </div>
        <TabsContent value="cargo_report" className="h-full">
          <ReportsTable />
        </TabsContent>
        <TabsContent value="party_report" className="h-full">
          <PartiesTable />
        </TabsContent>
      </Tabs>
    </div>
  );
};
